import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Calendar, MapPin, Tag, ArrowLeft, CheckCircle2, Clock, Navigation, Users, ExternalLink, FileText } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function parseISODateLocal(s) {
  // ISO date-only strings ("YYYY-MM-DD") get parsed by `new Date(...)` as UTC
  // midnight, which then rolls back a day in every US timezone when formatted.
  // Parse the parts manually so the date matches exactly what admins entered.
  if (!s || typeof s !== "string") return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (!m) return new Date(s);
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

function formatDateRange(start, end) {
  if (!start) return "";
  const opts = { month: "long", day: "numeric", year: "numeric" };
  try {
    const s = parseISODateLocal(start).toLocaleDateString("en-US", opts);
    if (!end || end === start) return s;
    const e = parseISODateLocal(end).toLocaleDateString("en-US", opts);
    return `${s} – ${e}`;
  } catch {
    return start;
  }
}

function formatTime(t) {
  // Expects "HH:MM" (24h). Returns 12h with am/pm.
  if (!t || !/^\d{1,2}:\d{2}$/.test(t)) return "";
  const [hStr, mStr] = t.split(":");
  let h = parseInt(hStr, 10);
  const suffix = h >= 12 ? "pm" : "am";
  h = h % 12;
  if (h === 0) h = 12;
  return `${h}:${mStr}${suffix}`;
}

function formatTimeRange(start, end) {
  const s = formatTime(start);
  const e = formatTime(end);
  if (s && e) return `${s} – ${e}`;
  return s || e || "";
}

export default function PublicEventDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [vendors, setVendors] = useState([]);

  useEffect(() => {
    setLoading(true);
    fetch(`${API}/events/${slug}`)
      .then((r) => {
        if (r.status === 404) {
          setNotFound(true);
          return null;
        }
        return r.json();
      })
      .then((data) => data && setEvent(data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
    // Load confirmed vendors in parallel (best-effort — silent on failure).
    fetch(`${API}/events/${slug}/vendors`)
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => setVendors(Array.isArray(data) ? data : []))
      .catch(() => setVendors([]));
  }, [slug]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in name, email, and a short message.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...form,
          subject: `Interested in ${event?.title || slug}`,
          event_context: slug,
        }),
      });
      if (!res.ok) throw new Error("Failed to submit");
      setSubmitted(true);
      toast.success("We've got your note — we'll be in touch soon.");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="editorial min-h-screen flex items-center justify-center" data-testid="event-loading">
        <p className="font-editorial text-2xl text-[color:var(--ed-muted)]">Loading…</p>
      </div>
    );
  }

  if (notFound || !event) {
    return (
      <div className="editorial min-h-screen flex flex-col items-center justify-center px-6 text-center" data-testid="event-not-found">
        <h1 className="text-5xl">We couldn&apos;t find that event.</h1>
        <p className="mt-4 font-body text-[color:var(--ed-muted)]">It may have ended, or the link may be slightly off.</p>
        <button onClick={() => navigate("/events")} className="btn-primary mt-8" data-testid="back-to-events-btn">
          <ArrowLeft className="w-4 h-4" /> Back to all events
        </button>
      </div>
    );
  }

  return (
    <div className="editorial min-h-screen" data-testid="public-event-detail-page">
      <Helmet>
        <title>{`${event.title} – Artisan Market & Studio`}</title>
        <meta name="description" content={event.tagline || event.title} />
      </Helmet>

      {/* Hero */}
      <section className="relative">
        {event.hero_image_url && (
          <div className="absolute inset-0 h-[70vh] overflow-hidden">
            <img
              src={event.hero_image_url}
              alt={event.title}
              className="w-full h-full object-cover"
            />
            {/* Bottom-weighted scrim: keeps the image bright up top,
                but guarantees strong contrast behind the title/meta chips. */}
            <div className="absolute inset-0 bg-gradient-to-t from-[#1E3227]/95 from-0% via-[#1E3227]/70 via-40% to-[#1E3227]/15 to-95%" />
            {/* Soft feather into the page cream so the hero blends without eating contrast */}
            <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-b from-transparent to-[color:var(--ed-cream)]" />
          </div>
        )}
        <div
          className="relative max-w-5xl mx-auto px-6 pt-24 pb-20 md:pt-32 md:pb-32 min-h-[70vh] flex flex-col justify-end text-[#FDF7EA]"
          style={{ textShadow: event.hero_image_url ? "0 2px 16px rgba(0,0,0,0.7), 0 1px 3px rgba(0,0,0,0.5)" : "none" }}
        >
          <Link to="/events" className="inline-flex items-center gap-2 text-sm mb-6 opacity-90 hover:opacity-100" data-testid="back-link">
            <ArrowLeft className="w-4 h-4" /> All events
          </Link>
          {event.tagline && (
            <span className="kicker text-[color:var(--ed-gold)]">{event.tagline}</span>
          )}
          <h1 className="mt-4 text-5xl md:text-7xl font-editorial leading-[1.02] max-w-3xl" data-testid="event-title">
            {event.title}
          </h1>
          <div className="mt-6 flex flex-wrap gap-x-6 gap-y-3 font-body text-base">
            {event.start_date && (
              <span className="inline-flex items-center gap-2" data-testid="event-date">
                <Calendar className="w-4 h-4" />
                {formatDateRange(event.start_date, event.end_date)}
              </span>
            )}
            {(event.start_time || event.end_time) && (
              <span className="inline-flex items-center gap-2" data-testid="event-time">
                <Clock className="w-4 h-4" />
                {formatTimeRange(event.start_time, event.end_time)}
              </span>
            )}
            {(event.venue || event.location) && (
              <span className="inline-flex items-center gap-2" data-testid="event-venue">
                <MapPin className="w-4 h-4" />
                {[event.venue, event.location].filter(Boolean).join(" · ")}
              </span>
            )}
            {event.price && (
              <span className="inline-flex items-center gap-2">
                <Tag className="w-4 h-4" />
                {event.price}
              </span>
            )}
          </div>
        </div>
      </section>

      {/* Body + Vendors Sidebar */}
      <section className="max-w-6xl mx-auto px-6 py-20">
        <div className="grid lg:grid-cols-3 lg:gap-14">
          {/* Main column */}
          <div className="lg:col-span-2 space-y-16">
            <div>
              <div className="flex items-center mb-8">
                <span className="rule" />
                <span className="kicker">The Story</span>
              </div>
              {event.description ? (
                <div
                  className="prose-editorial font-body text-lg"
                  dangerouslySetInnerHTML={{ __html: event.description }}
                />
              ) : (
                <p className="font-body text-lg text-[color:var(--ed-muted)]">
                  More details coming soon.
                </p>
              )}

              {Array.isArray(event.highlights) && event.highlights.length > 0 && (
                <div className="mt-14">
                  <div className="flex items-center mb-6">
                    <span className="rule" />
                    <span className="kicker">What to expect</span>
                  </div>
                  <ul className="space-y-3">
                    {event.highlights.map((h, i) => (
                      <li key={i} className="flex items-start gap-3 font-body text-lg">
                        <CheckCircle2 className="w-5 h-5 mt-1 text-[color:var(--ed-terracotta)] flex-shrink-0" />
                        <span>{h}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {slug === "festival-of-trees-2026" && (
              <div data-testid="event-terms-section">
                <div className="flex items-center mb-6">
                  <span className="rule" />
                  <span className="kicker">For our exhibitors</span>
                </div>
                <Link
                  to="/events/festival-of-trees-2026/terms"
                  className="group flex flex-col md:flex-row md:items-center gap-5 p-6 md:p-7 rounded-2xl border-2 border-[color:var(--ed-terracotta)]/30 bg-[color:var(--ed-terracotta)]/5 hover:bg-[color:var(--ed-terracotta)]/10 hover:border-[color:var(--ed-terracotta)]/60 transition-colors"
                  data-testid="event-terms-link"
                >
                  <div className="flex-shrink-0 w-14 h-14 rounded-full bg-[color:var(--ed-terracotta)] text-[#FDF7EA] flex items-center justify-center">
                    <FileText className="w-6 h-6" />
                  </div>
                  <div className="flex-1">
                    <p className="kicker text-[color:var(--ed-terracotta)]">Exhibitor Terms &amp; Conditions</p>
                    <p className="mt-1 font-editorial text-2xl md:text-3xl leading-tight text-[color:var(--ed-forest-dark)]">
                      Read the full participation agreement
                    </p>
                    <p className="mt-2 font-body text-[color:var(--ed-muted)]">
                      Booth rules, cancellation policy, insurance, and everything vendors need to know for November 20 &amp; 21, 2026.
                    </p>
                  </div>
                  <span className="hidden md:inline-flex items-center gap-2 text-[color:var(--ed-terracotta)] font-semibold group-hover:translate-x-1 transition-transform">
                    Open <ExternalLink className="w-4 h-4" />
                  </span>
                </Link>
              </div>
            )}

            {event.address && (
              <div data-testid="event-location-section">
                <div className="flex items-center mb-6">
                  <span className="rule" />
                  <span className="kicker">Where to find us</span>
                </div>
                <div className="space-y-4">
                  {event.venue && (
                    <p className="font-editorial text-3xl leading-tight">{event.venue}</p>
                  )}
                  <p className="font-body text-lg text-[color:var(--ed-muted)] flex items-start gap-2">
                    <MapPin className="w-5 h-5 mt-1 text-[color:var(--ed-terracotta)] flex-shrink-0" />
                    <span data-testid="event-address">{event.address}</span>
                  </p>
                  <div className="rounded-2xl overflow-hidden border border-[color:var(--ed-forest)]/15 shadow-sm bg-white">
                    <iframe
                      title={`Map of ${event.venue || event.title}`}
                      src={`https://www.google.com/maps?q=${encodeURIComponent(event.address)}&output=embed`}
                      width="100%"
                      height="360"
                      style={{ border: 0, display: "block" }}
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      allowFullScreen
                      data-testid="event-map-iframe"
                    />
                  </div>
                  <a
                    href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(event.address)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-[color:var(--ed-forest-dark)] font-semibold hover:text-[color:var(--ed-terracotta)] transition-colors"
                    data-testid="event-directions-link"
                  >
                    <Navigation className="w-4 h-4" /> Get directions
                  </a>
                </div>
              </div>
            )}
          </div>

          {/* Confirmed Vendors — sticky sidebar */}
          {vendors.length > 0 && (
            <aside className="lg:col-span-1 mt-12 lg:mt-0" data-testid="event-vendors-section">
              <div className="lg:sticky lg:top-24">
                <div className="card-warm p-0 overflow-hidden">
                  <div className="px-5 py-4 bg-[color:var(--ed-forest-dark)] text-[#FDF7EA]">
                    <div className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[color:var(--ed-gold)] font-body">
                      <Users className="w-3.5 h-3.5" />
                      Confirmed vendors
                    </div>
                    <p className="mt-1 font-editorial text-2xl leading-tight">
                      {vendors.length} {vendors.length === 1 ? "maker" : "makers"} joining us
                    </p>
                    {event.juried && (
                      <p className="mt-1 text-[11px] uppercase tracking-widest text-[#FDF7EA]/70">
                        Approved by the jury
                      </p>
                    )}
                  </div>
                  <ul className="divide-y divide-[color:var(--ed-forest)]/10" data-testid="event-vendors-list">
                    {vendors.map((v) => {
                      const label = v.name || v.compass_slug || "AMS maker";
                      if (v.compass_url) {
                        return (
                          <li key={v.id} data-testid={`event-vendor-${v.id}`}>
                            <a
                              href={v.compass_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="group flex items-center gap-3 px-5 py-3.5 transition-colors hover:bg-[color:var(--ed-terracotta)]/8 focus:outline-none focus-visible:bg-[color:var(--ed-terracotta)]/10"
                              data-testid={`event-vendor-link-${v.id}`}
                            >
                              <span className="w-2 h-2 rounded-full bg-[color:var(--ed-terracotta)] flex-shrink-0" aria-hidden="true" />
                              <span className="flex-1 font-body text-base text-[color:var(--ed-forest-dark)] group-hover:text-[color:var(--ed-terracotta)] font-medium">
                                {label}
                              </span>
                              <ExternalLink className="w-3.5 h-3.5 text-[color:var(--ed-terracotta)] opacity-60 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" />
                            </a>
                          </li>
                        );
                      }
                      return (
                        <li
                          key={v.id}
                          className="flex items-center gap-3 px-5 py-3.5"
                          data-testid={`event-vendor-${v.id}`}
                        >
                          <span className="w-1.5 h-1.5 rounded-full bg-[color:var(--ed-muted)]/60 flex-shrink-0" aria-hidden="true" />
                          <span className="flex-1 font-body text-base text-[color:var(--ed-forest-dark)]/75">
                            {label}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                  <div className="px-5 py-3 text-[11px] font-body text-[color:var(--ed-muted)] bg-[color:var(--ed-cream)]/40 border-t border-[color:var(--ed-forest)]/10">
                    Underlined names link to that maker&apos;s AMS Compass shop or their own website.
                  </div>
                </div>
              </div>
            </aside>
          )}
        </div>
      </section>

      {/* CTA / RSVP */}
      <section className="max-w-3xl mx-auto px-6 pb-32">
        <div className="card-warm">
          <div className="flex items-center mb-4">
            <span className="rule" />
            <span className="kicker">Reach out</span>
          </div>
          <h2 className="text-4xl leading-tight">
            Want to be part of {event.title}?
          </h2>
          <p className="mt-3 font-body text-[color:var(--ed-muted)]">
            Vendors, sponsors, and curious neighbors &mdash; drop us a note and we&apos;ll follow up personally.
          </p>

          {submitted ? (
            <div className="mt-8 flex items-center gap-3 text-[color:var(--ed-forest-dark)]" data-testid="event-form-success">
              <CheckCircle2 className="w-6 h-6" />
              <p className="font-body text-lg">Thank you &mdash; we&apos;ll be in touch shortly.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-8 grid gap-4" data-testid="event-contact-form">
              <input
                type="text"
                placeholder="Your name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full px-4 py-3 rounded-full bg-white border border-[color:var(--ed-forest)]/20 font-body focus:outline-none focus:border-[color:var(--ed-terracotta)]"
                data-testid="event-form-name"
                required
              />
              <input
                type="email"
                placeholder="Email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full px-4 py-3 rounded-full bg-white border border-[color:var(--ed-forest)]/20 font-body focus:outline-none focus:border-[color:var(--ed-terracotta)]"
                data-testid="event-form-email"
                required
              />
              <input
                type="tel"
                placeholder="Phone (optional)"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="w-full px-4 py-3 rounded-full bg-white border border-[color:var(--ed-forest)]/20 font-body focus:outline-none focus:border-[color:var(--ed-terracotta)]"
                data-testid="event-form-phone"
              />
              <textarea
                placeholder="Tell us a bit about your craft or what you're hoping for…"
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                rows={4}
                className="w-full px-4 py-3 rounded-2xl bg-white border border-[color:var(--ed-forest)]/20 font-body focus:outline-none focus:border-[color:var(--ed-terracotta)] resize-none"
                data-testid="event-form-message"
                required
              />
              <button
                type="submit"
                disabled={submitting}
                className="btn-primary justify-center w-full md:w-auto disabled:opacity-50"
                data-testid="event-form-submit"
              >
                {submitting ? "Sending…" : event.cta_label || "Send my note"}
              </button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}
